import { useState, useCallback } from 'react'

import { useAppDispatch } from 'redux/Hook'
import { updatePhrases } from 'redux/thunks/Themes'
import { useAlert } from 'components/Shared/Alert'
import type { Theme } from 'containers/TextAnalysis/AIThemes/ThemeKeyPhrasesModal/Types'
import { truncateText } from 'containers/TextAnalysis/Helpers/ThemesHelper'

interface LoadingState {
  isLoading: boolean
  message: string
}

export const useDeleteKeyPhrases = () => {
  const dispatch = useAppDispatch()
  const { showAlert } = useAlert()

  const [loadingState, setLoadingStateInternal] = useState<LoadingState>({
    isLoading: false,
    message: '',
  })

  const setLoadingState = useCallback(
    (isLoading: boolean, message = '') =>
      setLoadingStateInternal({ isLoading, message }),
    [],
  )

  const getKeyPhrases = (theme: Theme): string[] =>
    theme?.attributes?.query?.attributes?.key_phrases ?? []

  const executeDeleteKeyPhrase = useCallback(
    async (themeId: string, keyPhrase: string, keyPhrases: string[]) => {
      const updatedKeyPhrases = keyPhrases.filter((p) => p !== keyPhrase)

      await dispatch(
        updatePhrases({ themeId, keyPhrases: updatedKeyPhrases }),
      ).unwrap()
    },
    [dispatch],
  )

  const deleteKeyPhrase = useCallback(
    async (theme: Theme, keyPhrase: string): Promise<boolean> => {
      if (!keyPhrase || !theme) return false

      try {
        setLoadingState(true, 'Deleting key phrase...')

        const allKeyPhrases = getKeyPhrases(theme)
        await executeDeleteKeyPhrase(theme.id, keyPhrase, allKeyPhrases)

        showAlert({
          type: 'success',
          title: `Deleted key phrase "${truncateText(keyPhrase, 30)}" from ${
            theme.attributes.name
          }`,
        })

        return true
      } catch {
        return false
      } finally {
        setLoadingState(false)
      }
    },
    [setLoadingState, executeDeleteKeyPhrase, showAlert],
  )

  return { loadingState, deleteKeyPhrase }
}
